import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CheckCircle,
  CloudUpload,
  Download,
  Folder,
  Lock,
  LockOpen,
  Schedule,
  Visibility,
  Verified,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  useGetMemorialsQuery,
  useSubmitMemorialMutation,
  useOfficialSubmitMemorialMutation,
  useLazyDownloadMemorialQuery,
  type Memorial,
} from '../store/api/memorialsApi'
import {
  useGetEvidenceQuery,
  useGetEvidencePoolQuery,
  useCreateEvidenceFromPoolMutation,
  useDeleteEvidenceMutation,
  useOfficialSubmitEvidenceMutation,
  useLazyDownloadEvidenceQuery,
  type Evidence,
} from '../store/api/evidenceApi'
import {
  useGetR1TeamSummaryQuery,
  type R1Side,
} from '../store/api/r1SubmissionsApi'
import { PageHeader } from '../components/PageHeader'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { getApiErrorMessage } from '../utils/apiErrorMessage'
import { getClaimantName } from '../utils/caseDisplay'

const SIDE_VALUES: R1Side[] = ['scenario_a', 'scenario_b']

/** Theme cho 2 packet — phù hợp screenshot (hồng cho A, xanh cho B). */
const SIDE_THEME: Record<
  R1Side,
  { label: string; tone: 'pink' | 'blue'; bg: string; fg: string }
> = {
  scenario_a: { label: 'Hồ sơ 1', tone: 'pink', bg: '#fce4ec', fg: '#c2185b' },
  scenario_b: { label: 'Hồ sơ 2', tone: 'blue', bg: '#e3f2fd', fg: '#1565c0' },
}

/**
 * Map side → tên nguyên đơn.
 * GAP 2 (audit 2026-05-21): ưu tiên `case.scenario_a/b_claimant_name`, fallback
 * `plaintiff`/`defendant`. Dùng wrapper để các caller cũ nhận đầy đủ case obj.
 */
function plaintiffNameForSide(
  side: R1Side,
  plaintiff?: string,
  defendant?: string,
  scenario_a_name?: string | null,
  scenario_b_name?: string | null,
): string {
  return getClaimantName(
    {
      plaintiff,
      defendant,
      scenario_a_claimant_name: scenario_a_name ?? null,
      scenario_b_claimant_name: scenario_b_name ?? null,
    },
    side,
  )
}

export default function R1SubmissionPage() {
  const { user: currentUser } = useAppSelector((s) => s.auth)
  const [snack, setSnack] = useState<{ msg: string; ok: boolean } | null>(null)

  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()

  // Lọc chỉ cases dùng flow mới `dual_claimant_packets`.
  const dualCases = useMemo(
    () => cases.filter((c) => c.r1_flow_version === 'dual_claimant_packets'),
    [cases],
  )

  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const effectiveCaseId =
    selectedCaseId || (dualCases.length > 0 ? dualCases[0].id : '')
  const selectedCase = dualCases.find((c) => c.id === effectiveCaseId)

  // Suy team của user (student xem chính đội mình; BTC/judge có dropdown chọn).
  const userTeam = useMemo(
    () =>
      teams.find((t) => t.members.includes(currentUser?.id || '')),
    [teams, currentUser?.id],
  )

  const [adminSelectedTeamId, setAdminSelectedTeamId] = useState<string>('')
  const isAdminLike = ['secretary', 'support', 'judge', 'judge_president', 'admin'].includes(
    currentUser?.role || '',
  )

  const teamCandidates = useMemo(() => {
    if (!selectedCase) return []
    const ids = selectedCase.assigned_team_ids || []
    return teams.filter((t) => ids.includes(t.id))
  }, [selectedCase, teams])

  const effectiveTeamId =
    (isAdminLike ? adminSelectedTeamId : userTeam?.id) ||
    (isAdminLike && teamCandidates[0]?.id) ||
    ''

  // ── Tab side ─────────────────────────────────────────────────────────────
  const [activeSide, setActiveSide] = useState<R1Side>('scenario_a')

  // ── Data fetches ─────────────────────────────────────────────────────────
  const skipPacket = !effectiveCaseId || !effectiveTeamId
  const { data: teamSummary, refetch: refetchSummary } = useGetR1TeamSummaryQuery(
    skipPacket ? (undefined as any) : { case_id: effectiveCaseId, team_id: effectiveTeamId },
    { skip: skipPacket },
  )

  const { data: memorialsAll = [], refetch: refetchMemorials } = useGetMemorialsQuery(
    skipPacket
      ? (undefined as any)
      : { case_id: effectiveCaseId, team_id: effectiveTeamId },
    { skip: skipPacket },
  )

  const { data: evidenceAll = [], refetch: refetchEvidence } = useGetEvidenceQuery(
    skipPacket
      ? (undefined as any)
      : { case_id: effectiveCaseId, team_id: effectiveTeamId },
    { skip: skipPacket },
  )

  // P2-#15 (audit batch B): sau official_submit, refetch cả 3 nguồn dữ liệu
  // để FE phản ánh lock state + version mới nhất ngay lập tức.
  const refreshAll = () => {
    refetchSummary()
    refetchMemorials()
    refetchEvidence()
  }

  const { data: pool = [] } = useGetEvidencePoolQuery(
    effectiveCaseId || (undefined as any),
    { skip: !effectiveCaseId },
  )

  // ── Mutations ────────────────────────────────────────────────────────────
  const [submitMemorial, { isLoading: isUploadingMemorial }] = useSubmitMemorialMutation()
  const [officialSubmitMemorial] = useOfficialSubmitMemorialMutation()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [createEvidenceFromPool, { isLoading: isPickingEvidence }] =
    useCreateEvidenceFromPoolMutation()
  const [deleteEvidence] = useDeleteEvidenceMutation()
  const [officialSubmitEvidence, { isLoading: isLockingEvidence }] =
    useOfficialSubmitEvidenceMutation()
  const [downloadEvidence] = useLazyDownloadEvidenceQuery()

  // ── Per-side computed ────────────────────────────────────────────────────
  const officialMemorialBySide = useMemo(() => {
    const map: Record<R1Side, Memorial | null> = {
      scenario_a: null,
      scenario_b: null,
    }
    for (const side of SIDE_VALUES) {
      const sideMems = memorialsAll.filter((m) => m.r1_side === side)
      // Ưu tiên bản đã official, nếu không có thì bản mới nhất.
      const official = sideMems.find((m) => m.is_official_submission)
      const latest =
        official ||
        [...sideMems].sort((a, b) => {
          const av = a.version || 0
          const bv = b.version || 0
          return bv - av
        })[0] ||
        null
      map[side] = latest
    }
    return map
  }, [memorialsAll])

  const evidenceBySide = useMemo(() => {
    const map: Record<R1Side, Evidence[]> = { scenario_a: [], scenario_b: [] }
    for (const side of SIDE_VALUES) {
      map[side] = evidenceAll.filter((e) => e.r1_side === side)
    }
    return map
  }, [evidenceAll])

  function packetSideStatus(side: R1Side) {
    const packet = side === 'scenario_a' ? teamSummary?.scenario_a : teamSummary?.scenario_b
    const memorialOK = !!packet?.memorial_locked
    const evidenceCount = evidenceBySide[side].length
    const evidenceLocked = !!packet?.evidence_locked
    return { packet, memorialOK, evidenceCount, evidenceLocked }
  }

  // ── Render guards ────────────────────────────────────────────────────────
  if (dualCases.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
        <PageHeader
          icon={<Folder />}
          title="Hồ sơ khởi kiện"
          subtitle="Mỗi bộ gồm Đơn khởi kiện + Chứng cứ. Nộp đủ 2 bộ để hoàn tất vòng sơ loại."
        />
        <Alert severity="info">
          Chưa có cuộc thi nào dùng flow R1 mới (<code>dual_claimant_packets</code>).
          Cuộc thi cũ tiếp tục sử dụng trang Đơn khởi kiện cũ.
        </Alert>
      </Container>
    )
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  async function handleUploadMemorial(side: R1Side, file: File) {
    if (!effectiveCaseId || !effectiveTeamId) return
    const fd = new FormData()
    fd.append('case_id', effectiveCaseId)
    fd.append('team_id', effectiveTeamId)
    fd.append('r1_side', side)
    fd.append('title', file.name)
    fd.append('file', file)
    try {
      await submitMemorial(fd).unwrap()
      setSnack({ msg: `Đã tải lên đơn khởi kiện cho ${SIDE_THEME[side].label}.`, ok: true })
      refreshAll()
    } catch (err) {
      setSnack({ msg: getApiErrorMessage(err) || 'Tải lên thất bại', ok: false })
    }
  }

  async function handleLockMemorial(side: R1Side) {
    const mem = officialMemorialBySide[side]
    if (!mem) return
    try {
      await officialSubmitMemorial(mem.id).unwrap()
      setSnack({ msg: `Đã nộp chính thức đơn khởi kiện ${SIDE_THEME[side].label}.`, ok: true })
      refreshAll()
    } catch (err) {
      setSnack({ msg: getApiErrorMessage(err) || 'Khóa thất bại', ok: false })
    }
  }

  async function handlePickPoolItem(side: R1Side, documentId: string) {
    if (!effectiveCaseId || !effectiveTeamId) return
    try {
      await createEvidenceFromPool({
        case_id: effectiveCaseId,
        team_id: effectiveTeamId,
        document_id: documentId,
        r1_side: side,
      }).unwrap()
      refreshAll()
    } catch (err) {
      setSnack({ msg: getApiErrorMessage(err) || 'Chọn chứng cứ thất bại', ok: false })
    }
  }

  async function handleUnpickEvidence(eid: string) {
    try {
      await deleteEvidence(eid).unwrap()
      refreshAll()
    } catch (err) {
      setSnack({ msg: getApiErrorMessage(err) || 'Bỏ chọn thất bại', ok: false })
    }
  }

  async function handleLockEvidence(side: R1Side) {
    if (!effectiveCaseId || !effectiveTeamId) return
    if (evidenceBySide[side].length === 0) {
      setSnack({ msg: 'Chưa chọn chứng cứ nào — không thể nộp chính thức.', ok: false })
      return
    }
    try {
      await officialSubmitEvidence({
        case_id: effectiveCaseId,
        team_id: effectiveTeamId,
        r1_side: side,
      }).unwrap()
      setSnack({ msg: `Đã nộp chính thức chứng cứ ${SIDE_THEME[side].label}.`, ok: true })
      refreshAll()
    } catch (err) {
      setSnack({ msg: getApiErrorMessage(err) || 'Khóa thất bại', ok: false })
    }
  }

  async function handleDownloadMemorial(id: string, fileName: string) {
    try {
      const blob = await downloadMemorial(id).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setSnack({ msg: 'Tải xuống thất bại', ok: false })
    }
  }

  async function handleDownloadEvidence(id: string, fileName: string) {
    try {
      const blob = await downloadEvidence(id).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setSnack({ msg: 'Tải xuống thất bại', ok: false })
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const sideStatus = packetSideStatus(activeSide)
  const officialMemorial = officialMemorialBySide[activeSide]
  const selectedEvidence = evidenceBySide[activeSide]
  const selectedEvidenceIds = new Set(selectedEvidence.map((e) => e.id))
  const sideTheme = SIDE_THEME[activeSide]

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
      <PageHeader
        icon={<Folder />}
        title="Hồ sơ khởi kiện"
        subtitle="Mỗi bộ gồm Đơn khởi kiện + Chứng cứ. Nộp đủ 2 bộ để hoàn tất vòng sơ loại."
      />

      {/* Filter cuộc thi + (admin) chọn đội */}
      <Paper sx={getFilterPaperSx} elevation={0}>
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Lọc theo cuộc thi</InputLabel>
            <Select
              value={effectiveCaseId}
              label="Lọc theo cuộc thi"
              onChange={(e) => setSelectedCaseId(e.target.value)}
            >
              {dualCases.map((c, idx) => (
                <MenuItem key={c.id} value={c.id}>
                  {idx + 1} — {c.title.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isAdminLike && (
            <FormControl size="small" fullWidth>
              <InputLabel>Đội thi (BTC)</InputLabel>
              <Select
                value={effectiveTeamId}
                label="Đội thi (BTC)"
                onChange={(e) => setAdminSelectedTeamId(e.target.value)}
              >
                {teamCandidates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Paper>

      {!effectiveTeamId && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Chưa xác định được đội thi. {isAdminLike ? 'Hãy chọn đội ở dropdown phía trên.' : 'Bạn chưa thuộc đội nào trong cuộc thi này.'}
        </Alert>
      )}

      {effectiveTeamId && (
        <>
          {/* Tabs 2 packet */}
          <Paper
            sx={(t) => ({
              mt: 2,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              border: `1px solid ${t.palette.divider}`,
              background: t.palette.background.paper,
            })}
            elevation={0}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              gap={2}
            >
              <Tabs
                value={activeSide}
                onChange={(_, v: R1Side) => setActiveSide(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flex: 1, '& .MuiTab-root': { textTransform: 'none' } }}
              >
                {SIDE_VALUES.map((side) => {
                  const t = SIDE_THEME[side]
                  const st = packetSideStatus(side)
                  const plName = plaintiffNameForSide(
                    side,
                    selectedCase?.plaintiff,
                    selectedCase?.defendant,
                    selectedCase?.scenario_a_claimant_name,
                    selectedCase?.scenario_b_claimant_name,
                  )
                  return (
                    <Tab
                      key={side}
                      value={side}
                      sx={{
                        borderRadius: 2,
                        mx: 0.5,
                        background: t.bg,
                        color: t.fg,
                        fontWeight: 600,
                        '&.Mui-selected': {
                          background: t.bg,
                          color: t.fg,
                          boxShadow: `inset 0 -3px 0 ${t.fg}`,
                        },
                      }}
                      label={
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Box>{`${t.label} — Nguyên đơn: ${plName}`}</Box>
                          <Chip
                            size="small"
                            label={st.memorialOK ? 'Đơn ✓' : 'Thiếu đơn'}
                            color={st.memorialOK ? 'success' : 'warning'}
                            variant={st.memorialOK ? 'filled' : 'outlined'}
                          />
                          <Chip
                            size="small"
                            label={`${st.evidenceCount} CC${st.evidenceLocked ? ' ✓' : ''}`}
                            color={st.evidenceLocked ? 'success' : 'default'}
                            variant={st.evidenceLocked ? 'filled' : 'outlined'}
                          />
                        </Stack>
                      }
                    />
                  )
                })}
              </Tabs>
              <Chip
                icon={<Schedule fontSize="small" />}
                label="Đang mở nhận hồ sơ"
                variant="outlined"
                sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
              />
            </Stack>
          </Paper>

          {/* Section ĐƠN KHỞI KIỆN */}
          <Paper
            sx={(t) => ({
              mt: 2,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              border: `1px solid ${t.palette.divider}`,
            })}
            elevation={0}
          >
            <Typography
              variant="overline"
              sx={{ color: sideTheme.fg, letterSpacing: 1, fontWeight: 700 }}
            >
              Đơn khởi kiện
            </Typography>
            <Divider sx={{ mb: 1.5, mt: 0.5 }} />
            {officialMemorial ? (
              <Box
                sx={(t) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderLeft: `4px solid ${sideTheme.fg}`,
                  background: sideTheme.bg,
                  borderRadius: 1,
                })}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700}>{officialMemorial.title || officialMemorial.file_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(officialMemorial.file_size / 1024 / 1024).toFixed(2)} MB · Phiên bản v
                    {officialMemorial.version}
                  </Typography>
                  <Stack direction="row" gap={1} mt={0.5}>
                    <Chip size="small" label={`Nguyên đơn: ${plaintiffNameForSide(activeSide, selectedCase?.plaintiff, selectedCase?.defendant, selectedCase?.scenario_a_claimant_name, selectedCase?.scenario_b_claimant_name)}`} />
                    <Chip size="small" color="success" label={officialMemorial.status} />
                    {sideStatus.memorialOK && (
                      <Chip
                        size="small"
                        icon={<Lock fontSize="small" />}
                        color="primary"
                        label="Đã khóa"
                      />
                    )}
                    {officialMemorial.is_official_submission && (
                      <Chip
                        size="small"
                        icon={<Verified fontSize="small" />}
                        color="success"
                        label="Bản được chọn"
                      />
                    )}
                  </Stack>
                </Box>
                <Tooltip title="Tải xuống">
                  <IconButton
                    onClick={() => handleDownloadMemorial(officialMemorial.id, officialMemorial.file_name)}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box
                sx={(t) => ({
                  border: `1px dashed ${t.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                })}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography color="text.secondary">
                    Chưa có đơn khởi kiện cho {sideTheme.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Soạn đơn từ góc độ {plaintiffNameForSide(activeSide, selectedCase?.plaintiff, selectedCase?.defendant, selectedCase?.scenario_a_claimant_name, selectedCase?.scenario_b_claimant_name)} là nguyên đơn, sau đó upload lên đây.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={isUploadingMemorial}
                >
                  Nộp Đơn – {sideTheme.label}
                  <input
                    type="file"
                    accept=".pdf,.docx"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) handleUploadMemorial(activeSide, f)
                      e.target.value = ''
                    }}
                  />
                </Button>
              </Box>
            )}
            {officialMemorial && !sideStatus.memorialOK && (
              <Box sx={{ mt: 1.5, textAlign: 'right' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Lock />}
                  onClick={() => handleLockMemorial(activeSide)}
                >
                  Xác nhận bản chính thức
                </Button>
              </Box>
            )}
          </Paper>

          {/* Section CHỨNG CỨ */}
          <Paper
            sx={(t) => ({
              mt: 2,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              border: `1px solid ${t.palette.divider}`,
            })}
            elevation={0}
          >
            <Typography
              variant="overline"
              sx={{ color: sideTheme.fg, letterSpacing: 1, fontWeight: 700 }}
            >
              Chứng cứ
            </Typography>
            <Divider sx={{ mb: 1.5, mt: 0.5 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
              {/* Cột trái: pool */}
              <Box sx={{ flex: 1 }}>
                <Paper sx={getTablePaperSx} elevation={0}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={700}>Chứng cứ từ đề thi</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Bấm "Chọn" để đính kèm vào {sideTheme.label}
                      </Typography>
                    </Box>
                    <Chip size="small" label={`${pool.length} tài liệu`} />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tiêu đề</TableCell>
                          <TableCell align="right">Thao tác</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pool.map((p) => {
                          const picked = selectedEvidence.find(
                            (e) =>
                              (e.tags || []).includes(
                                `from_pool_document:${p.document_id}`,
                              ),
                          )
                          return (
                            <TableRow key={p.document_id}>
                              <TableCell>
                                <Stack direction="row" alignItems="center" gap={1}>
                                  {picked ? (
                                    <CheckCircle fontSize="small" color="success" />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        border: '2px solid #ccc',
                                      }}
                                    />
                                  )}
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {p.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {p.file_name}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                {picked ? (
                                  <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    disabled={sideStatus.evidenceLocked}
                                    onClick={() => handleUnpickEvidence(picked.id)}
                                  >
                                    Đã chọn
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={
                                      isPickingEvidence || sideStatus.evidenceLocked
                                    }
                                    onClick={() =>
                                      handlePickPoolItem(activeSide, p.document_id)
                                    }
                                  >
                                    Chọn
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                        {pool.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Typography color="text.secondary" align="center">
                                Pool tài liệu chưa có mục nào.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Box>

              {/* Cột phải: chứng cứ đã chọn */}
              <Box sx={{ flex: 1 }}>
                <Paper sx={getTablePaperSx} elevation={0}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ p: 1.5 }}
                  >
                    <Box>
                      <Typography fontWeight={700}>Chứng cứ đã chọn</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {sideTheme.label} — Nguyên đơn: {plaintiffNameForSide(activeSide, selectedCase?.plaintiff, selectedCase?.defendant, selectedCase?.scenario_a_claimant_name, selectedCase?.scenario_b_claimant_name)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={sideStatus.evidenceLocked ? 'success' : 'default'}
                      icon={sideStatus.evidenceLocked ? <Lock fontSize="small" /> : undefined}
                      label={sideStatus.evidenceLocked ? 'Đã nộp chính thức' : 'Chưa nộp chính thức'}
                    />
                  </Stack>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tiêu đề</TableCell>
                          <TableCell align="right">Thao tác</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedEvidence.map((e) => (
                          <TableRow key={e.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {e.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {e.file_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadEvidence(e.id, e.file_name)}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {selectedEvidence.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Typography align="center" color="text.secondary">
                                Chọn thêm từ pool bên trái để hoàn tất {sideTheme.label}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                    }}
                  >
                    {sideStatus.evidenceLocked ? (
                      <Chip
                        icon={<Lock fontSize="small" />}
                        label={`Đã khóa — ${selectedEvidence.length} mục`}
                        color="success"
                      />
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<CheckCircle />}
                        disabled={isLockingEvidence || selectedEvidence.length === 0}
                        onClick={() => handleLockEvidence(activeSide)}
                      >
                        Nộp chính thức {sideTheme.label}
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Stack>
          </Paper>
        </>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? (
          <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Container>
  )
}
